/**
 * Cómo se muestra el dinero de cada país: símbolo, decimales y separadores.
 * Chile factura en pesos enteros; Perú y México con centavos. `MX$` y no `$`
 * para que un monto mexicano no se confunda con uno en pesos chilenos.
 */
const COUNTRY_MONEY = {
  CL: { iso: 'CL', symbol: '$', decimals: 0, locale: 'es-CL' },
  PE: { iso: 'PE', symbol: 'S/', decimals: 2, locale: 'en-US' },
  MX: { iso: 'MX', symbol: 'MX$', decimals: 2, locale: 'en-US' },
}

// Nombres con que llega el país: '🇵🇪 Perú' desde el 360, 'México' o 'Mexico'
// desde la base (el nombre se corrige en la spec 084).
const COUNTRY_BY_NAME = [
  ['peru', 'PE'],
  ['mexico', 'MX'],
  ['chile', 'CL'],
]

export default class CurrencyUtils {
  /**
   * Formato de dinero del país de un usuario o de un reembolso.
   *
   * @param {string} country ISO ('CL', 'PE', 'MX') o nombre, con o sin tilde o bandera.
   * @returns {{ iso: string, symbol: string, decimals: number, locale: string }}
   *   Chile cuando no se reconoce, como se asumía hasta ahora.
   */
  static countryMoney(country) {
    return COUNTRY_MONEY[CurrencyUtils.countryIso(country) || 'CL']
  }

  /**
   * ISO del país a partir de su código o su nombre ("Perú", "🇵🇪 Perú",
   * "México", "Mexico").
   *
   * @param {string} country ISO o nombre.
   * @returns {string|null} 'CL', 'PE', 'MX', o null si no es un país que opere Pawer.
   */
  static countryIso(country) {
    const raw = String(country ?? '').trim()
    if (COUNTRY_MONEY[raw.toUpperCase()]) return raw.toUpperCase()
    const name = raw.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    const match = COUNTRY_BY_NAME.find(([key]) => name.includes(key))
    return match ? match[1] : null
  }

  /**
   * Lee un monto escrito a mano. En CLP solo cuentan los dígitos, como siempre.
   * En otra moneda acepta coma o punto decimal y hasta 2 decimales: antes
   * "24,7" se leía como 247 y así nació multiplicado por 10 el contrato de
   * Crehana CL (spec 084).
   *
   * Con coma, la coma es el decimal y los puntos son miles ("1.234,56").
   * Sin coma, un punto seguido de 1 o 2 dígitos al final es decimal ("24.7") y
   * cualquier otro punto es de miles ("1.234").
   *
   * @param {string|number} text Lo que escribió la persona.
   * @param {boolean} allowDecimals false para CLP.
   * @returns {number|null} null si no hay ningún dígito.
   */
  static parseAmountInput(text, allowDecimals) {
    const raw = String(text ?? '').trim()
    if (!/\d/.test(raw)) return null
    if (!allowDecimals) return Number(raw.replace(/\D/g, ''))
    let normalized
    if (raw.includes(',')) {
      normalized = raw.replace(/\./g, '').replace(',', '.')
    } else if (/\.\d{1,2}$/.test(raw) && raw.split('.').length === 2) {
      normalized = raw
    } else {
      normalized = raw.replace(/\./g, '')
    }
    const [integerPart, fractionPart = ''] = normalized.replace(/[^\d.]/g, '').split('.')
    const cents = fractionPart.slice(0, 2)
    return Number(cents ? `${integerPart || '0'}.${cents}` : integerPart)
  }

  /**
   * Monto con el símbolo y los decimales de su país, p. ej. `MX$ 1,234.50`.
   *
   * @param {number|string} value Monto.
   * @param {string} country ISO o nombre del país.
   * @param {string} [separator=' '] Lo que va entre el símbolo y el número.
   */
  static formatCountryAmount(value, country, separator = ' ') {
    const money = CurrencyUtils.countryMoney(country)
    const amount = Number(value).toLocaleString(money.locale, {
      minimumFractionDigits: money.decimals,
      maximumFractionDigits: money.decimals,
    })
    return `${money.symbol}${separator}${amount}`
  }

  static formatPriceFromNumber(price, thousandsSeparator = '.') {
    return price
      .toString()
      .split('')
      .reverse()
      .reduce((acc, char, index) => {
        return char + (index && index % 3 === 0 ? thousandsSeparator : '') + acc;
      }, '');
  }

  static cleanCurrencyFormat(price) {
    return price.replace(/[^0-9]/g, '');
  }

  /**
   * Keeps digits plus a single decimal point, trimming the fractional part to
   * `decimals` digits. A trailing point is preserved so the user can keep typing.
   */
  static cleanDecimalCurrencyFormat(price, decimals = 2) {
    const [integerPart, ...fractionParts] = String(price)
      .replace(/[^0-9.]/g, '')
      .split('.');
    if (fractionParts.length === 0) return integerPart;
    return `${integerPart}.${fractionParts.join('').slice(0, decimals)}`;
  }

  /**
   * Turns a formatted amount being typed (e.g. `1.234,5` with `,` as decimal
   * separator) into `1234.5`, the form `formatDecimalPrice` and `Number` read.
   * Any other character, the thousands separator included, is dropped.
   */
  static toPlainAmount(text, decimalSeparator = ',') {
    const [integerPart, ...fractionParts] = String(text ?? '')
      .split(decimalSeparator)
      .map(part => part.replace(/\D/g, ''));
    const integer = integerPart.replace(/^0+(?=\d)/, '');
    return fractionParts.length ? `${integer}.${fractionParts.join('')}` : integer;
  }

  /**
   * Formats an amount that may carry decimals, e.g. `1234.5` -> `1,234.5` with
   * the Peruvian convention (comma thousands, point decimal).
   */
  static formatDecimalPrice(
    price,
    { thousandsSeparator = ',', decimalSeparator = '.', decimals = 2 } = {}
  ) {
    const clean = CurrencyUtils.cleanDecimalCurrencyFormat(price, decimals);
    if (clean === '') return '';
    const [integerPart, fractionPart] = clean.split('.');
    const formattedInteger = CurrencyUtils.formatPriceFromNumber(
      integerPart || '0',
      thousandsSeparator
    );
    if (fractionPart === undefined) return formattedInteger;
    return `${formattedInteger}${decimalSeparator}${fractionPart}`;
  }
}
